import type {Values} from '@wix/devzai-utils-common';
import {
    enumReduce,
    stringGenerateUnique,
    stringTrimPrefix,
    urlJoin,
    urlOverrideQueryParams,
    valueConvertWhenNotNullable
} from '@wix/devzai-utils-common';
import {DoppeSupportedWixTpas} from './doppe-supported-wix-tpas';
import {DoppeViewerLang} from "../doppe-viewer/doppe-viewer-lang";

export const DoppeHtmlSiteUriMappingType = {
    Default: 'Default',
    Store: 'Store',
    Services: 'Services',
    Ecom: 'Ecom',
    Blog: 'Blog',
    Events: 'Events',
    PricingPlans: 'PricingPlans'
} as const;

export type DoppeHtmlSiteUriMappingType = Values<typeof DoppeHtmlSiteUriMappingType>;

const DoppeHtmlSiteUriMappingTypesSuggestedBasePaths = {
    [DoppeHtmlSiteUriMappingType.Default]: ['page', '_'],
    [DoppeHtmlSiteUriMappingType.Store]: ['online-store'],
    [DoppeHtmlSiteUriMappingType.Ecom]: ['ecom', 'ecommerce'],
    [DoppeHtmlSiteUriMappingType.Blog]: ['posts'],
    [DoppeHtmlSiteUriMappingType.Services]: ['bookings'],
    [DoppeHtmlSiteUriMappingType.Events]: ['events-list'],
    [DoppeHtmlSiteUriMappingType.PricingPlans]: ['pricing-plans-list']
} satisfies Record<DoppeHtmlSiteUriMappingType, string[]>;

export function doppeHtmlSiteUriMappingTypeGetSuggestedBasePath (
    uriMappingType: DoppeHtmlSiteUriMappingType,
    options: {
        usedBasePaths: string[];
    }
): string {
    const {
        usedBasePaths
    } = options;

    return stringGenerateUnique(DoppeHtmlSiteUriMappingTypesSuggestedBasePaths[uriMappingType], usedBasePaths, (iteration, value) => {
        if (iteration === 0) {
            return `_${value}`;
        } else if (iteration === 1) {
            return `__${value}`;
        } else {
            return `${value}${iteration - 1}`
        }
    })
}

export const DoppeHtmlSiteRequiringWixAppsIds = enumReduce(DoppeSupportedWixTpas, [
    DoppeSupportedWixTpas.StoresNew,
    DoppeSupportedWixTpas.Stores,
    DoppeSupportedWixTpas.Blog,
    DoppeSupportedWixTpas.Booking,
    DoppeSupportedWixTpas.Events,
    DoppeSupportedWixTpas.PricingPlans,
])

export type DoppeHtmlSiteRequiringWixAppsIds = Values<typeof DoppeHtmlSiteRequiringWixAppsIds>;

const WixAppsIdsToHtmlSiteUriMappingType = {
    [DoppeHtmlSiteRequiringWixAppsIds.StoresNew]: DoppeHtmlSiteUriMappingType.Store,
    [DoppeHtmlSiteRequiringWixAppsIds.Stores]: DoppeHtmlSiteUriMappingType.Ecom,
    [DoppeHtmlSiteRequiringWixAppsIds.Blog]: DoppeHtmlSiteUriMappingType.Blog,
    [DoppeHtmlSiteRequiringWixAppsIds.Booking]: DoppeHtmlSiteUriMappingType.Services,
    [DoppeHtmlSiteRequiringWixAppsIds.Events]: DoppeHtmlSiteUriMappingType.Events,
    [DoppeHtmlSiteRequiringWixAppsIds.PricingPlans]: DoppeHtmlSiteUriMappingType.PricingPlans,
} satisfies Record<DoppeHtmlSiteRequiringWixAppsIds, DoppeHtmlSiteUriMappingType>;

export function dopppeHtmlSiteUriMappingTypesResolveFromWixAppId (
    wixAppId: DoppeHtmlSiteRequiringWixAppsIds
): DoppeHtmlSiteUriMappingType {
    return WixAppsIdsToHtmlSiteUriMappingType[wixAppId];
}

export type DoppeHtmlSiteUriMappings = Partial<Record<DoppeHtmlSiteUriMappingType, string>>;

export type DoppeHtmlSiteInfo = {
    htmlSiteUriMapping: DoppeHtmlSiteUriMappings;
    htmlSiteUrl: string;
}

export function doppeHtmlSiteResolvePageUrlFromBusinessEntityPageUrl (
    options: {
        doppeHtmlSiteInfo: DoppeHtmlSiteInfo;
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType;
        businessEntityPageUrl: string;
        viewerLang: DoppeViewerLang;
        showMobileView?: boolean;
    }
) {
    const {
        doppeHtmlSiteInfo,
        htmlSiteUriMappingType,
        businessEntityPageUrl,
        viewerLang,
        showMobileView
    } = options;

    return doppeHtmlSiteResolvePageUrl({
        doppeHtmlSiteInfo: doppeHtmlSiteInfo,
        htmlSiteUriMappingType: htmlSiteUriMappingType,
        pagePath: stringTrimPrefix(businessEntityPageUrl, doppeHtmlSiteInfo.htmlSiteUrl),
        viewerLang: viewerLang,
        showMobileView: showMobileView
    })
}

export function doppeHtmlSiteResolveBlogPostPageUrl (
    options: {
        doppeHtmlSiteInfo: DoppeHtmlSiteInfo;
        slug: string;
        viewerLang: DoppeViewerLang;
        showMobileView?: boolean;
    }
) {
    return doppeHtmlSiteResolvePageUrlFromBusinessEntityPageUrl({
        doppeHtmlSiteInfo: options.doppeHtmlSiteInfo,
        businessEntityPageUrl: `post/${options.slug}`,
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType.Blog,
        viewerLang: options.viewerLang,
        showMobileView: options.showMobileView
    })
}


export function doppeHtmlSiteResolveEventPageUrl (
    options: {
        doppeHtmlSiteInfo: DoppeHtmlSiteInfo;
        slug: string;
        viewerLang: DoppeViewerLang;
        showMobileView?: boolean;
    }
) {
    return doppeHtmlSiteResolvePageUrlFromBusinessEntityPageUrl({
        doppeHtmlSiteInfo: options.doppeHtmlSiteInfo,
        businessEntityPageUrl: `event-details/${options.slug}`,
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType.Events,
        viewerLang: options.viewerLang,
        showMobileView: options.showMobileView
    })
}

/**
 * Resolves the RSVP form URL for an event.
 * Example: https://example.com/events/event-details/{slug}/form
 */
export function doppeHtmlSiteResolveEventRsvpFormUrl (
    options: {
        doppeHtmlSiteInfo: DoppeHtmlSiteInfo;
        slug: string;
        viewerLang: DoppeViewerLang;
        showMobileView?: boolean;
    }
) {
    return doppeHtmlSiteResolvePageUrlFromBusinessEntityPageUrl({
        doppeHtmlSiteInfo: options.doppeHtmlSiteInfo,
        businessEntityPageUrl: `event-details/${options.slug}/form`,
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType.Events,
        viewerLang: options.viewerLang,
        showMobileView: options.showMobileView
    });
}

/**
 * Resolves the ticket checkout form URL for an event with a reservation.
 * Example: https://example.com/events/event-details/{slug}/ticket-form?reservationId={reservationId}
 */
export function doppeHtmlSiteResolveEventTicketFormUrl (
    options: {
        doppeHtmlSiteInfo: DoppeHtmlSiteInfo;
        slug: string;
        reservationId: string;
        viewerLang: DoppeViewerLang;
        showMobileView?: boolean;
    }
) {
    const baseUrl = doppeHtmlSiteResolvePageUrlFromBusinessEntityPageUrl({
        doppeHtmlSiteInfo: options.doppeHtmlSiteInfo,
        businessEntityPageUrl: `event-details/${options.slug}/ticket-form`,
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType.Events,
        viewerLang: options.viewerLang,
        showMobileView: options.showMobileView
    });

    if (!baseUrl) {
        return null;
    }

    return urlOverrideQueryParams(baseUrl, {
        reservationId: options.reservationId
    });
}

export function doppeHtmlSiteResolvePricingPlanPageUrl (
    options: {
        doppeHtmlSiteInfo: DoppeHtmlSiteInfo;
        viewerLang: DoppeViewerLang;
        showMobileView?: boolean;
    }
) {
    return doppeHtmlSiteResolvePageUrlFromBusinessEntityPageUrl({
        doppeHtmlSiteInfo: options.doppeHtmlSiteInfo,
        businessEntityPageUrl: `plans-pricing`,
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType.PricingPlans,
        viewerLang: options.viewerLang,
        showMobileView: options.showMobileView
    })
}

export function doppeHtmlSiteResolveServicePageUrl (
    options: {
        doppeHtmlSiteInfo: DoppeHtmlSiteInfo;
        pageURL: string;
        viewerLang: DoppeViewerLang;
        showMobileView?: boolean;
    }
) {
    return doppeHtmlSiteResolvePageUrlFromBusinessEntityPageUrl({
        doppeHtmlSiteInfo: options.doppeHtmlSiteInfo,
        businessEntityPageUrl: options.pageURL,
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType.Services,
        viewerLang: options.viewerLang,
        showMobileView: options.showMobileView
    })
}

export function doppeHtmlSiteResolveProductPageUrl (
    options: {
        doppeHtmlSiteInfo: DoppeHtmlSiteInfo;
        productPageUrl: string;
        viewerLang: DoppeViewerLang;
        showMobileView?: boolean;
    }
) {
    return doppeHtmlSiteResolvePageUrlFromBusinessEntityPageUrl({
        doppeHtmlSiteInfo: options.doppeHtmlSiteInfo,
        businessEntityPageUrl: options.productPageUrl,
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType.Store,
        viewerLang: options.viewerLang,
        showMobileView: options.showMobileView
    })
}

export function doppeHtmlSiteResolvePageUrl (
    options: {
        doppeHtmlSiteInfo: DoppeHtmlSiteInfo;
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType;
        pagePath: string;
        viewerLang: DoppeViewerLang
        showMobileView?: boolean;
    }
) {
    const {
        doppeHtmlSiteInfo,
        htmlSiteUriMappingType,
        pagePath,
        viewerLang,
        showMobileView,
    } = options;

    return valueConvertWhenNotNullable(doppeHtmlSiteInfo.htmlSiteUriMapping[htmlSiteUriMappingType], uriMapping => {
        return urlOverrideQueryParams(urlJoin(doppeHtmlSiteInfo.htmlSiteUrl, uriMapping, pagePath), {
            lang: viewerLang === DoppeViewerLang.English ? null : viewerLang,
            showMobileView: showMobileView ? 'true' : null
        });
    }) ?? null
}

export function doppeHtmlSiteResolvePricingPlansPageURL (_doppeHtmlSiteInfo: DoppeHtmlSiteInfo, _viewerLang: DoppeViewerLang, _showMobileView?: boolean) {
    // ai should replace with the actual pricing plans page URL
    return '';
}

export function doppeHtmlSiteResolveEventsPageURL(doppeHtmlSiteInfo: DoppeHtmlSiteInfo, viewerLang: DoppeViewerLang, showMobileView?: boolean) {
    return doppeHtmlSiteResolvePageUrl({
        doppeHtmlSiteInfo: doppeHtmlSiteInfo,
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType.Events,
        pagePath: '/event-list',
        viewerLang: viewerLang,
        showMobileView: showMobileView
    });
}

export function doppeHtmlSiteResolveAllPostsPageUrl (
    doppeHtmlSiteInfo: DoppeHtmlSiteInfo,
    viewerLang: DoppeViewerLang,
    showMobileView?: boolean
) {
    return doppeHtmlSiteResolvePageUrl({
        doppeHtmlSiteInfo: doppeHtmlSiteInfo,
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType.Blog,
        pagePath: '/blog',
        viewerLang: viewerLang,
        showMobileView: showMobileView
    })
}

export function doppeHtmlSiteResolveAllProductsPageUrl (
    doppeHtmlSiteInfo: DoppeHtmlSiteInfo,
    viewerLang: DoppeViewerLang,
    showMobileView?: boolean
) {
    return doppeHtmlSiteResolvePageUrl({
        doppeHtmlSiteInfo: doppeHtmlSiteInfo,
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType.Store,
        pagePath: '/category/all-products',
        viewerLang: viewerLang,
        showMobileView: showMobileView
    })
}

export function doppeHtmlSiteResolveCartPageUrl (
    doppeHtmlSiteInfo: DoppeHtmlSiteInfo,
    viewerLang: DoppeViewerLang,
    showMobileView?: boolean
) {
    return doppeHtmlSiteResolvePageUrl({
        doppeHtmlSiteInfo: doppeHtmlSiteInfo,
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType.Store,
        pagePath: '/cart-page',
        viewerLang: viewerLang,
        showMobileView: showMobileView
    })
}

/**
 * Resolves the booking form URL for a specific service (typically courses).
 * The booking form page uses query parameters to load the correct service context.
 * Example: https://example.com/services/booking-form?bookings_serviceId={id}&bookings_timezone={tz}
 */
export function doppeHtmlSiteResolveBookingFormUrl (
    options: {
        doppeHtmlSiteInfo: DoppeHtmlSiteInfo;
        serviceId: string;
        timezone: string;
        viewerLang: DoppeViewerLang;
        showMobileView?: boolean;
    }
) {
    const baseUrl = doppeHtmlSiteResolvePageUrlFromBusinessEntityPageUrl({
        doppeHtmlSiteInfo: options.doppeHtmlSiteInfo,
        businessEntityPageUrl: `booking-form`,
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType.Services,
        viewerLang: options.viewerLang,
        showMobileView: options.showMobileView
    });

    if (!baseUrl) {
        return null;
    }

    return urlOverrideQueryParams(baseUrl, {
        bookings_serviceId: options.serviceId,
        bookings_timezone: options.timezone
    });
}

export function doppeHtmlSiteResolveBookOnlinePageUrl (
    doppeHtmlSiteInfo: DoppeHtmlSiteInfo,
    viewerLang: DoppeViewerLang,
    showMobileView?: boolean
) {
    return doppeHtmlSiteResolvePageUrl({
        doppeHtmlSiteInfo: doppeHtmlSiteInfo,
        htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType.Services,
        pagePath: '/book-online',
        viewerLang: viewerLang,
        showMobileView: showMobileView
    })
}

export const DoppeHtmlSitePagesType = {
    Store: 'Store',
    Events: 'Events',
    BookingServices: 'BookingServices',
    Blog: 'Blog',
    PricingPlans: 'PricingPlans',
    Cart: 'Cart',
} as const;

export type DoppeHtmlSitePagesType = Values<typeof DoppeHtmlSitePagesType>;